<template>
  <slot></slot>
  <modal :id="modalId" :title="title">
    <form :id="formId" :action="action" method="POST">

      <div class="form-group row">
        <label class="col-form-label col-sm-2">Current Status:</label>
        <div class="col-sm-10">
          <p class="form-control-plaintext form-control-sm" v-html="status"></p>
        </div>
      </div>
      <div class="form-group row" :class="{'has-error': hasError['new_status']}">
        <label for="new_status" class="col-form-label col-sm-2">New Status:</label>
        <div class="col-sm-10">
          <chosen id="new_status" name="new_status" v-model="data.new_status"
                  :options="statusList"
                  :chosen-options="{allow_single_deselect: true}"
                  placeholder="Select new status..." class="form-control form-control-sm"></chosen>
        </div>
      </div>
      <div class="form-group row" v-show="error">
        <div class="col-sm-10 offset-sm-2">
          <span class="text-danger">{{ error }}</span>
        </div>
      </div>
      <div class="form-group row" v-show="reopen">
        <label for="new_status" class="col-form-label col-sm-2">Reopen:</label>
        <div class="col-sm-10">
          <label class="mr-1">
            <input type="checkbox" name="reopen_as_supplement"> Reopen as a supplement
          </label>
        </div>
      </div>
      <div class="form-group row" :class="{'has-error': hasError['reasons']}">
        <label for="reasons" class="col-form-label col-sm-2">Reason(s):</label>
        <div class="col-sm-10">
          <chosen id="reasons" name="reasons[]" v-model="data.reasons" :options="changeReasons" multiple
                  placeholder="Select reason(s) of the status change..." class="form-control"></chosen>
        </div>
      </div>
      <div id="other_reason_container" class="form-group row" :class="{'has-error': hasError['other_reason']}"
           v-show="otherReasonVisible">
        <label class="col-form-label col-sm-2">Other Reason:</label>
        <div class="col-sm-10">
            <textarea class="form-control form-control-sm" name="other_reason" rows="5"
                      placeholder="Enter the reason of the status change..." v-model="data.other_reason"></textarea>
        </div>
      </div>
      <div class="form-group row">
        <label class="col-sm-2 col-form-label">Notify:</label>
        <div class="col-sm-10 mt-1">
          <label class="mr-1">
            <input type="checkbox" name="notify_team_lead">
            Account Manager
          </label>
          <label class="mr-1">
            <input type="checkbox" name="notify_adjuster">
            Adjuster
          </label>
          <label class="mr-1">
            <input type="checkbox" name="notify_examiner">
            Examiner
          </label>
          <label class="mr-1">
            <input type="checkbox" name="notify_others" v-model="data.notify_others">
            Others
          </label>
        </div>
      </div>
      <div id="other_emails_container" v-show="otherEmailsVisible" class="form-group row"
           :class="{'has-error': hasError['other_emails']}">
        <div class="offset-sm-2 col-sm-10">
          <email-typeahead
            v-model="data.other_emails"
            fieldname="other_emails">
          </email-typeahead>
        </div>
      </div>

      <input type="hidden" name="_method" value="PATCH">
      <input type="hidden" name="_token" :value="$csrf_token">
    </form>

    <template v-slot:footer>
      <button type="button" class="btn btn-default" @click="hideModal"><i
          class="fa fa-fw fa-close"></i> Close
      </button>
      <button type="submit" class="btn btn-primary" :disabled="saveButtonDisabled" :form="formId">
        <i class="fa fa-fw fa-save"></i> Save changes
      </button>
    </template>


  </modal>
</template>

<script>
import HasModal from "./../mixins/HasModal";
export default {

  /**
   *
   * Modal dialog allows to change claim's status and select notification option.
   *
   * Input properties:
   *
   * modalId       - custom id for modal component.
   * action        - form action URL.
   * title         - form title.
   * status        - new status.
   * statusList    - status list for selection.
   * changeReasons - reasons for status change.
   * claimId
   * teamId
   * adjusterId
   * examinerId
   *
   * Usage examples:
   *
   * <change-claim-status-modal modal-id="changeClaimStatusModal"
   *                            action="{{ route('claims.change-status', compact('claim')) }}"
   *                            title="Change Claim Status"
   *                            claim-id="{{ $claim->id }}"
   *                            team-id="{{ $claim->team_id }}"
   *                            adjuster-id="{{ $claim->adjuster_id }}"
   *                            examiner-id="{{ $claim->examiner_id }}"
   *                            status="{{ $claim->present()->status }}"
   *                            :status-list="{{ json_encode($change_status['status_list']) }}"
   *                            :change-reasons="{{ json_encode($change_status['reasons']) }}">
   *   <a class="btn btn-sm btn-success" href="#" v-modal="'changeClaimStatusModal'">
   *     <i class="fa fa-fw fa-retweet"></i> Change Status
   *   </a>
   * </change-claim-status-modal>
   *
   */

  mixins: [HasModal],

  props: {

    // Modal dialog id.
    modalId: {
      required: false,
      type: String,
      default: 'changeClaimStatusModal'
    },

    // Form action URL.
    action: {
      type: String,
      default: ""
    },

    // Form title.
    title: {
      type: String,
      default: ""
    },

    //
    claimId: {
      required: true,
      type: Number,
    },

    //
    teamId: {
      type: String,
      default: ""
    },

    //
    adjusterId: {
      type: String,
      default: ""
    },

    //
    examinerId: {
      type: String,
      default: ""
    },

    //
    status: {
      required: false,
      type: String,
      default: ""
    },

    //
    closed: {
      required: true,
      type: Boolean,
      default: false
    },

    //
    statusList: {
      required: true,
      type: Object,
    },

    //
    changeReasons: {
      required: true,
      type: Object,
    },

  },

  watch: {
    'data.new_status': {
      handler(val) {
        this.hasError['new_status'] = !val;
        this.error = '';

        let new_status = parseInt(val);
        this.reopen = (this.closed && new_status > 0 && new_status <= 100);

        let prereq = this.prereqs[val];
        if (prereq !== undefined) {
          if (prereq.team_required && !this.hasTeam) {
            this.hasError['new_status'] = true;
            this.error = 'To use this status, assign a team to this claim.';
          }
          if (prereq.full_staff_required && !this.hasFullStaff) {
            this.hasError['new_status'] = true;
            this.error = 'To use this status, assign a team and an adjuster to this claim.';
          }
        }
      },
    },

    'data.reasons': {
      handler() {
        this.hasError['reasons'] = !this.data.reasons.length;

        if (this.hasOther) {
          this.data.other_reason = '';
        }
      },
      deep: true
    },

    'data.other_reason': {
      handler(val) {
        this.hasError['other_reason'] = !val;
      },
    },

    'data.other_emails': {
      handler(val) {
        this.hasError['other_emails'] = !val;
      },
    },

    'data.notify_others': {
      handler(val) {
        if (!val) {
          this.data.other_emails = '';
        }
      },
    },
  },

  /**
   * Variables (models).
   */
  data() {
    return {
      data: {
        reasons: [],
        new_status: "",
        other_reason: "",
        other_emails: "",
        notify_others: false,
      },
      error: "",
      reopen: false,
      prereqs: {
        '20': {
          team_required: true
        },
        '30': {
          full_staff_required: true
        },
        '40': {
          full_staff_required: true
        },
        '50': {
          full_staff_required: true
        },
        '60': {
          full_staff_required: true
        },
        '70': {
          full_staff_required: true
        },
        '80': {
          full_staff_required: true
        },
        '90': {
          full_staff_required: true
        }
      },

      hasError: {
        'new_status': true,
        'reasons': true,
        'other_reason': true,
        'other_emails': true,
      }
    }
  },

  /**
   * Computed values
   */
  computed: {

    hasTeam() {
      return this.teamId !== undefined;
    },

    hasFullStaff() {
      return this.hasTeam && this.adjusterId !== undefined;
    },

    hasOther() {
      return this.data.reasons.includes('other');
    },

    otherReasonVisible() {
      return this.hasOther;
    },

    otherEmailsVisible() {
      return this.data.notify_others;
    },

    saveButtonDisabled() {

      let enabled = this.data.new_status && !!this.data.reasons.length && !this.error;

      if (this.hasOther) {
        enabled &= !!(this.data.other_reason && this.data.other_reason.trim().length);
      }

      if (this.data.notify_others) {
        enabled &= !!(this.data.other_emails && this.data.other_emails.trim().length);
      }

      let prereq = this.prereqs[this.data.new_status];
      if (prereq !== undefined) {
        enabled &= !prereq.team_required || (prereq.team_required && this.hasTeam);
        enabled &= !prereq.full_staff_required || (prereq.full_staff_required && this.hasFullStaff);
      }

      return !enabled;
    },

    formId() {
      return this.modalId + "-form"
    }

  },
}
</script>
